import { Button, comboboxClassNames, Divider, inputClassNames, makeStyles } from '@fluentui/react-components';
import { datePickerClassNames } from '@fluentui/react-datepicker-compat';
import { AddRegular } from '@fluentui/react-icons';
import { endOfYear, format, startOfYear } from 'date-fns';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { OptionGuidResponse } from '../../api';
import { MpmTemplateOption } from '../../api/hooks/OptionsApi';
import { MpmProject, MpmProjectEntryFormValues } from '../../types/MpmTypes';
import ResponsiveRowWrapper from '../layout/ResponsiveRowWrapper';
import Stack from '../layout/Stack';
import DatePicker from './DatePicker';
import OptionTypeCombo from './OptionTypeCombo';
import TextInput from './TextInput';

const useStyles = makeStyles({
    detailRow: {
        // The fields wrap (flex-wrap: wrap from ResponsiveRowWrapper): one row when there's room, two or three
        // when narrow (e.g. the Teams side panel). Each field's wrapper gets a min-width its control is actually
        // usable at (fieldWide / fieldDate below), and the control fills it and may shrink to it (`min-width: 0`).
        // Without that, Fluent's Combobox/Input kept their intrinsic minimum (~250px) and overflowed into the
        // neighbouring field, and the last date picker clipped at the card edge.
        // Fluent's published static class names (comboboxClassNames etc.): the controls don't expose a width API.
        [`& .${inputClassNames.root}, & .${comboboxClassNames.root}, & .${datePickerClassNames.root}`]: {
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
        },
        [`& .${comboboxClassNames.input}, & .${inputClassNames.input}`]: { minWidth: 0, width: '100%' },
    },
    fieldWide: { flex: '2 1 220px', minWidth: '220px' },
    fieldDate: { flex: '1 1 160px', minWidth: '160px' },
    // Billing description is a sentence: give it the whole row so it isn't cut off.
    fieldFullRow: { flex: '1 1 100%', minWidth: 0 },
    addButtonRow: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
});

type MpmProjectEntryRowProps = {
    onAdd: (project: MpmProject) => void;
    templateOptions: MpmTemplateOption[];
    templateOptionsLoaded: boolean;
    staffOptions: OptionGuidResponse[];
    staffOptionsLoaded: boolean;
    yearEnd?: string;
};

const getDefaultValues = (yearEnd?: string): MpmProjectEntryFormValues => {
    const now = new Date();
    const currentYear = now.getFullYear();

    let coverageEnd: string;
    let coverageStart: string;

    if (yearEnd) {
        const [month, day] = yearEnd.split('/').map(Number);
        coverageEnd = format(new Date(currentYear, month - 1, day), 'yyyy-MM-dd');
        // Coverage starts the day AFTER the prior year-end (matches backend MpmHelper.CalculateCoverageDates).
        coverageStart = format(new Date(currentYear - 1, month - 1, day + 1), 'yyyy-MM-dd');
    } else {
        coverageEnd = format(endOfYear(now), 'yyyy-MM-dd');
        coverageStart = format(startOfYear(now), 'yyyy-MM-dd');
    }

    return {
        templateId: '',
        projectName: '',
        billingDescription: '',
        projectOwnerId: '',
        coverageStart,
        coverageEnd,
        projectStart: format(now, 'yyyy-MM-dd'),
    };
};

const MpmProjectEntryRow = ({
    onAdd,
    templateOptions,
    templateOptionsLoaded,
    staffOptions,
    staffOptionsLoaded,
    yearEnd,
}: MpmProjectEntryRowProps) => {
    const styles = useStyles();
    const { control, handleSubmit, reset, watch, setValue, clearErrors } = useForm<MpmProjectEntryFormValues>({
        defaultValues: getDefaultValues(yearEnd),
    });

    const templateId = watch('templateId');

    useEffect(() => {
        clearErrors();
        const template = templateOptions.find((o) => o.id === templateId);
        const currentYear = new Date().getFullYear().toString();
        setValue('projectName', (template?.name ?? '').replace('[YYYY]', currentYear));
        setValue('billingDescription', (template?.billingDescription ?? '').replace('[YYYY]', currentYear));
    }, [templateId, templateOptions, setValue, clearErrors]);

    useEffect(() => {
        if (!yearEnd) return;
        const currentYear = new Date().getFullYear();
        const [month, day] = yearEnd.split('/').map(Number);
        setValue('coverageEnd', format(new Date(currentYear, month - 1, day), 'yyyy-MM-dd'));
        // Coverage starts the day AFTER the prior year-end (matches backend MpmHelper.CalculateCoverageDates).
        setValue('coverageStart', format(new Date(currentYear - 1, month - 1, day + 1), 'yyyy-MM-dd'));
    }, [yearEnd, setValue]);

    const onSubmit = (data: MpmProjectEntryFormValues) => {
        const templateName = templateOptions.find((o) => o.id === data.templateId)?.name ?? '';
        const projectOwnerName = staffOptions.find((o) => o.id === data.projectOwnerId)?.name ?? '';

        onAdd({
            templateId: data.templateId,
            templateName,
            projectName: data.projectName,
            billingDescription: data.billingDescription,
            projectOwnerId: data.projectOwnerId,
            projectOwnerName,
            coverageStart: data.coverageStart,
            coverageEnd: data.coverageEnd,
            projectStart: data.projectStart,
        });

        reset(getDefaultValues(yearEnd));
    };

    return (
        <Stack flexDirection="column" alignItems="stretch">
            <ResponsiveRowWrapper>
                <OptionTypeCombo
                    control={control}
                    name="templateId"
                    label="Project Template"
                    options={templateOptions}
                    doneLoading={templateOptionsLoaded}
                    rules={{ required: 'Template is required' }}
                />
            </ResponsiveRowWrapper>
            {templateId && (
                <>
                    <Divider />
                    <ResponsiveRowWrapper className={styles.detailRow}>
                        <TextInput
                            control={control}
                            name="projectName"
                            wrapperClassName={styles.fieldWide}
                            label="Project Name"
                            rules={{ required: 'Project name is required' }}
                        />
                        <OptionTypeCombo
                            control={control}
                            name="projectOwnerId"
                            wrapperClassName={styles.fieldWide}
                            label="Project Owner"
                            options={staffOptions}
                            doneLoading={staffOptionsLoaded}
                            rules={{ required: 'Project owner is required' }}
                        />
                        <DatePicker
                            control={control}
                            name="projectStart"
                            wrapperClassName={styles.fieldDate}
                            label="Project Start"
                            rules={{ required: 'Project start is required' }}
                        />
                        <DatePicker
                            control={control}
                            name="coverageStart"
                            wrapperClassName={styles.fieldDate}
                            label="Coverage Start"
                            rules={{ required: 'Coverage start is required' }}
                        />
                        <DatePicker
                            control={control}
                            name="coverageEnd"
                            wrapperClassName={styles.fieldDate}
                            label="Coverage End"
                            rules={{ required: 'Coverage end is required' }}
                        />
                    </ResponsiveRowWrapper>
                    <ResponsiveRowWrapper className={styles.detailRow}>
                        <TextInput
                            control={control}
                            name="billingDescription"
                            label="Billing Description"
                            wrapperClassName={styles.fieldFullRow}
                        />
                    </ResponsiveRowWrapper>
                    <div className={styles.addButtonRow}>
                        <Button appearance="primary" icon={<AddRegular />} onClick={handleSubmit(onSubmit)}>
                            Add Project
                        </Button>
                    </div>
                </>
            )}
        </Stack>
    );
};

export default MpmProjectEntryRow;
